import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { styled } from "styled-components";
import { useDebouncedCallback } from "use-debounce";

import { knowledgeApiSlice } from "api/knowledgeApi.slice";
import { FilterButton, MasterDetailLayout } from "common/components";
import { UIInput } from "common/components/form/UIInput";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { getIsAuthenticated } from "domains/authentication/selectors";
import { InteractiveDataDetailSection } from "domains/knowledgeData/components/InteractiveDataDetailSection";
import { InteractiveDataList } from "domains/knowledgeData/components/InteractiveDataList";
import { activateModal } from "domains/modal/actions";
import { searchSlice } from "domains/search/search.slice";

const StyledInput = styled(UIInput)`
  margin-bottom: 1rem;
`;

const StyledSearchInputContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

export const InteractiveDataSearchPage = () => {
  const track = useTrack();
  const dispatch = useDispatch();
  const translate = useTranslate();
  const { dataId } = useParams();

  const isAuthenticated = useSelector(getIsAuthenticated);
  const currentSearchQuery = useSelector((state) => state.search.currentSearchQuery);
  const selectedSources = useSelector((state) => state.search.sources);

  const [searchInput, setSearchInput] = useState(currentSearchQuery);

  const { updateSearchQuery } = searchSlice.actions;

  const debouncedUpdateSearchQuery = useDebouncedCallback((searchQuery: string) => {
    dispatch(updateSearchQuery({ query: searchQuery }));
  }, 1000);

  useEffect(() => {
    debouncedUpdateSearchQuery(searchInput);
  }, [currentSearchQuery, debouncedUpdateSearchQuery, dispatch, searchInput, updateSearchQuery]);

  const {
    data: paginatedInteractiveDataSearchResults,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = knowledgeApiSlice.endpoints.searchInteractiveData.useInfiniteQuery({
    searchQuery: currentSearchQuery,
    sourceNames: selectedSources.map((source) => source.name),
  });

  const interactiveDataSearchResults = paginatedInteractiveDataSearchResults?.pages.flat() ?? [];

  const interactiveData = dataId
    ? interactiveDataSearchResults.find((interactiveData) => interactiveData.id === dataId)
    : undefined;

  useEffect(() => {
    if (interactiveData)
      track({
        entityId: interactiveData.id,
        entityName: interactiveData.title,
        entityType: "Interactive data",
        name: "Browse page",
        page: "Search",
        subpage: "Knowledge detail",
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactiveData?.id, track]);

  const loadNextPage = useCallback(async () => {
    if (currentSearchQuery && hasNextPage && !isFetching) await fetchNextPage();
  }, [currentSearchQuery, hasNextPage, isFetching, fetchNextPage]);

  const getLink = (id: string) => `/search/interactive-data/${id}`;

  const knowledgeList = (
    <InteractiveDataList
      interactiveData={
        interactiveDataSearchResults.length === 0 && interactiveData ? [interactiveData] : interactiveDataSearchResults
      }
      selectedId={interactiveData?.id}
      trackPage="Discover"
      getToLink={getLink}
      isPending={isFetching}
      hasNextPage={hasNextPage}
      showEmptyMessage={!!currentSearchQuery}
      emptyMessage={translate("app.search.results.no_results")}
      loadNextPage={loadNextPage}
      lockTiles={!isAuthenticated}
    />
  );

  const shareLink = `${import.meta.env.VITE_WEBSITE_URL}${!interactiveData?.id ? "/search/interactive-data" : getLink(interactiveData.id)}`;

  const getFilterCount = () => {
    let count = 0;

    count += selectedSources.length;

    return count;
  };

  const openFilterModal = () => dispatch(activateModal({ id: "KnowledgeSearchFilter" }));

  return (
    <MasterDetailLayout
      headerElement={
        <StyledSearchInputContainer>
          <StyledInput
            name="interactiveDataSeach"
            clear={() => setSearchInput("")}
            onChange={setSearchInput}
            placeholder={translate("app.discover.topic.tabs.knowledge.filter.search_title")}
            value={searchInput}
          />
          <FilterButton count={getFilterCount()} onClick={openFilterModal} />
        </StyledSearchInputContainer>
      }
      listTitle={translate("app.discover.topic.tabs.interactive_data.title")}
      listComponent={knowledgeList}
      listBackNavigation={{
        label: translate("app.search.title"),
        path: "/search",
      }}
      detailBackNavigation={{
        label: translate("app.discover.topic.tabs.interactive_data.title"),
        path: "/search/interactive-data",
      }}
      mainView={dataId ? "right" : "left"}
    >
      <InteractiveDataDetailSection
        id={interactiveData?.id}
        shareLink={shareLink}
        topicBack={{ term: "app.discover.topic.tabs.interactive_data.title" }}
      />
    </MasterDetailLayout>
  );
};
